/** @jsxImportSource @emotion/react */
import { ForwardedRef, forwardRef, useState, memo, ImgHTMLAttributes } from 'react';
import { getUrlWithToken } from '../../../utils/getMediaURLWithToken';
import { useGetToken } from '../../../hooks/useGetToken';
import { css, Interpolation, Theme } from '@emotion/react';
import QSTypography from '../typography';
import ErrorIcon from '../../../icons/ErrorIcon';
import { CircularProgress } from '@mui/material';

interface Props {
  readonly placeholderSrc?: string;
  readonly imgProps?: Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & { css?: Interpolation<Theme> };
  readonly loaderProps?: Interpolation<Theme>;
  readonly src: string;
  readonly retry?: boolean;
  readonly maxRetries?: number;
}

const ProgressiveImage = memo(
  forwardRef(
    (
      { placeholderSrc, src, imgProps = {}, loaderProps = {}, retry = true, maxRetries = 4 }: Props,
      ref: ForwardedRef<HTMLImageElement>
    ) => {
      const [currentSrc, setCurrentSrc] = useState(src);
      const [retryCount, setRetryCount] = useState(0);
      const [isImageLoaded, setIsImageLoaded] = useState(false);
      const [hasFailed, setHasFailed] = useState(false);

      const MAX_RETRIES = maxRetries;
      const RETRY_DELAY = 2000;
      const token = useGetToken();

      const handleImageError = () => {
        if (retry && retryCount < MAX_RETRIES && currentSrc) {
          setTimeout(() => {
            setRetryCount(retryCount + 1);
            setCurrentSrc((prevSrc) => {
              const url = new URL(prevSrc);
              url.searchParams.set('retry', (retryCount + 1).toString());
              url.searchParams.set('token', token ?? '');
              return url.toString();
            });
          }, RETRY_DELAY);
        } else {
          setHasFailed(true);
          setCurrentSrc(getUrlWithToken(placeholderSrc ?? '', token ?? ''));
        }
      };

      const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        setIsImageLoaded(true);
        imgProps.onLoad?.(e);
      };

      return (
        <>
          {loaderProps && ((!hasFailed && !isImageLoaded) || (hasFailed && retry)) && (
            <span
              css={[
                css`
                  display: inline-flex;
                  justify-content: center;
                  align-items: center;
                  gap: 4px;
                  pointer-events: none;
                  z-index: 1;
                `,
                loaderProps,
              ]}
            >
              {hasFailed ? (
                <>
                  <ErrorIcon width={16.667} height={16} flex-shrink={0} color="#fff" />
                  <QSTypography
                    id="progressive-image-error-text"
                    css={css`
                      color: #fff;
                      font-family: Inter;
                      font-size: 12px;
                      font-style: normal;
                      font-weight: 500;
                      line-height: normal;
                    `}
                  >
                    Could not load thumbnail
                  </QSTypography>
                </>
              ) : (
                <CircularProgress
                  size={16}
                  css={css`
                    position: relative;
                    color: white;
                  `}
                />
              )}
            </span>
          )}
          {!isImageLoaded && (
            <img
              alt=""
              ref={ref}
              {...(imgProps || {})}
              style={{
                ...imgProps.style,
                height: imgProps.style?.height,
                width: imgProps.style?.width,
              }}
              src={getUrlWithToken(placeholderSrc ?? '', token ?? '')}
              css={imgProps.css}
            />
          )}
          <img
            alt=""
            ref={ref}
            {...(imgProps || {})}
            style={{
              ...imgProps.style,
              height: !isImageLoaded ? '0px' : imgProps.style?.height,
              width: !isImageLoaded ? '0px' : imgProps.style?.width,
            }}
            src={currentSrc ? getUrlWithToken(currentSrc, token ?? '') : ''}
            onLoad={handleImageLoad}
            onError={handleImageError}
            css={imgProps.css}
          />
        </>
      );
    }
  ),
  (prevProps: Props, nextProps: Props) => {
    return (
      prevProps.src === nextProps.src &&
      prevProps.placeholderSrc === nextProps.placeholderSrc &&
      Object.keys(prevProps.imgProps?.style ?? {}) === Object.keys(nextProps.imgProps?.style ?? {}) &&
      prevProps.loaderProps === nextProps.loaderProps
    );
  }
);

export default ProgressiveImage;
